import { Link } from 'react-router-dom';
import { Crown, Loader2 } from 'lucide-react';

export type SubscriptionPlanRow = {
  id: string;
  code: string;
  name: string;
  amount_paise: number;
  credits_per_period: number;
  is_paid: boolean;
};

type Props = {
  plans: SubscriptionPlanRow[];
  hasPaidSubscription: boolean;
  /** DB id of the user's current paid plan; omit or null on free tier */
  currentPlanId?: string | null;
  busyPlanId: string | null;
  error: string | null;
  onSubscribe: (planId: string) => void;
  /** When true, all CTAs disabled (e.g. maintenance) */
  forceManageOnly?: boolean;
  /** Show sign-in link instead of subscribe */
  isGuest?: boolean;
};

function formatInrPaise(paise: number): string {
  const n = paise / 100;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

function planButtonLabel(
  p: SubscriptionPlanRow,
  currentPlanId: string | null | undefined,
  hasPaidSubscription: boolean,
  paidPlans: SubscriptionPlanRow[]
): string {
  if (p.id === currentPlanId) return 'Current plan';
  if (!hasPaidSubscription || !currentPlanId) return 'Subscribe';
  const cur = paidPlans.find((x) => x.id === currentPlanId);
  const curCredits = cur?.credits_per_period ?? 0;
  if (p.credits_per_period > curCredits) return 'Upgrade';
  if (p.credits_per_period < curCredits) return 'Change at next renewal';
  return 'Subscribe';
}

export function SubscriptionPlanGrid({
  plans,
  hasPaidSubscription,
  currentPlanId = null,
  busyPlanId,
  error,
  onSubscribe,
  forceManageOnly = false,
  isGuest = false,
}: Props) {
  const paid = plans.filter((p) => p.is_paid && p.amount_paise > 0);

  return (
    <div className="w-full space-y-4">
      {error ? (
        <p className="rounded-lg border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">{error}</p>
      ) : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {paid.map((p) => (
          <div
            key={p.id}
            className={`relative flex flex-col rounded-2xl border border-subtle bg-elevated p-5 text-left transition ${
              p.id === currentPlanId ? 'ring-1 ring-brand/40' : ''
            }`}
          >
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand/15 text-brand">
                <Crown className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <h3 className="text-base font-semibold text-th-primary">{p.name}</h3>
                <p className="text-xs text-th-tertiary">{p.credits_per_period.toLocaleString()} credits / month</p>
              </div>
            </div>
            <p className="mb-1 text-2xl font-bold tabular-nums text-th-primary">{formatInrPaise(p.amount_paise)}</p>
            <p className="mb-4 text-xs text-th-secondary">Monthly subscription. Credits reset each billing period (no rollover).</p>
            {isGuest ? (
              <Link
                to="/login"
                className="mt-auto inline-flex items-center justify-center rounded-lg border border-subtle bg-surface px-4 py-2.5 text-sm font-medium text-th-primary hover:bg-overlay"
              >
                Sign in to subscribe
              </Link>
            ) : (
              <button
                type="button"
                disabled={!!busyPlanId || forceManageOnly || p.id === currentPlanId}
                onClick={() => onSubscribe(p.id)}
                className="mt-auto inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-surface hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busyPlanId === p.id ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Opening…
                  </>
                ) : (
                  planButtonLabel(p, currentPlanId, hasPaidSubscription, paid)
                )}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
